import { DOCUMENT } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { flatMap } from 'lodash';
import { filter, first, Subject, takeUntil } from 'rxjs';

import { PolicySelectors } from '@amfam/policy/data-access';
import { VehicleCoveragesFacade } from '@amfam/policy/vehicle-coverages/data-access';
import { agentQuery } from '@amfam/shared/utility/agent';
import { BrandSelectors } from '@amfam/shared/utility/brand';
import { CopyService } from '@amfam/shared/utility/shared-services';
import {
  DockingBarService,
  DsModalService,
  ErrorLevels,
  ErrorObj,
  PageTitleService
} from '@amfam/ui-kit';

@Component({
  selector: 'amfam-change-coverage-container',
  templateUrl: './change-coverage-container.component.html',
  styleUrls: ['./change-coverage-container.component.scss'],
  standalone: false
})
export class ChangeCoverageContainerComponent implements OnInit, OnDestroy {
  public exitFlowModalId = this.modalService.createModalId('exitFlowModalId');
  public earlyExitFlowModalId = this.modalService.createModalId('earlyExitFlowModalId');
  public agentSelectionModalId = this.modalService.createModalId('agentSelectionModalId');
  public agents$ = this.store.select(agentQuery.getRealAgents);

  public isPartner$ = this.store.select(BrandSelectors.selectIsPartner);
  public hasError: boolean;

  public errorList: Array<ErrorObj> = [];

  private stop$: Subject<void> = new Subject<void>();
  private changeCoverageText = this.copyService.getCopy('policy.changeCoverage.pageTitle');
  private changeCoveragePageHeader = this.copyService.getCopy('policy.changeCoverage.pageHeader');
  private vehicleId: string;

  constructor(
    private pageTitle: PageTitleService,
    private dockingBarService: DockingBarService,
    private copyService: CopyService,
    private modalService: DsModalService,
    private store: Store,
    private route: ActivatedRoute,
    private vehicleCoveragesFacade: VehicleCoveragesFacade,
    @Inject(DOCUMENT) private document: Document
  ) {}

  ngOnInit(): void {
    this.pageTitle.set(this.changeCoverageText);
    this.dockingBarService.registerHeading(this.changeCoveragePageHeader);
    this.loadCoverages();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onActivate(componentReference: any) {
    this.handleError(componentReference);
  }

  scrollToTop() {
    this.document.defaultView?.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }

  loadCoverages() {
    this.store
      .select(PolicySelectors.selectAdvanceAutoPolicies)
      .pipe(
        filter(policies => policies && policies.length > 0),
        first()
      )
      .subscribe(policies => {
        const vehicleId = this.route.snapshot.params['id'];
        const vehicles = flatMap(policies, policy => policy.vehicles);
        const vehicle = vehicles.find(v => +v.vehicleId === +vehicleId);
        if (vehicle) this.vehicleCoveragesFacade.changeCoverage(vehicle);
      });
  }

  ngOnDestroy() {
    this.vehicleCoveragesFacade.resetAll();
    this.stop$.next();
    this.stop$.complete();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private handleError(componentReference: any) {
    this.hasError = false;
    if (componentReference.errorEvent) {
      //Below will subscribe to the error emitter
      componentReference.errorEvent
        .pipe(takeUntil(this.stop$))
        .subscribe(({ hasError, errorMessage } = { hasError: true, errorMessage: '' }) => {
          this.hasError = hasError;
          if (hasError) this.scrollToTop();
          if (errorMessage) {
            this.errorList = [
              {
                errorMessage: errorMessage,
                errorLevel: ErrorLevels.HIGH
              }
            ];
          }
        });
    }
  }
}
